/* eslint-disable promise/prefer-await-to-callbacks */
import { notification } from 'antd';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ConnectionModel from '@mm/state/model/ConnectionModel';
import { ConnectionTagModel } from '@mm/state/model/ConnectionTagModel';
import TagModel from '@mm/state/model/TagModel';
import { StoreState } from '@mm/state/store-interfaces';

const OWN_TAG = 'customers-ai';

export const useTags = (
  connection: ConnectionModel,
  cb: (items: TagModel[]) => void,
) => {
  const [tags, setTags] = useState<TagModel[]>([]);

  const availableTags = useSelector(
    (state: StoreState) => state.salesForce.tags,
  );
  const tagsEnabled = useSelector(
    (state: StoreState) => state.salesForce.tagsEnabled,
  );

  // Only letters, numbers, spaces, dashes, and underscores
  const validTag = (tag: TagModel): boolean => {
    const regex = /^[a-zA-Z0-9\s_-]+$/;
    return tag.name.length > 0 && regex.test(tag.name);
  };

  const tagWarning = (tag: TagModel): void => {
    notification.warning({
      message: `Tag "${tag.name}" is invalid.
Tags can only contain letters, numbers, spaces, dashes, and underscores,
and must contain at least one letter or number`,
    });
  };

  const tagsValid = (): boolean => {
    let valid = true;

    tags.forEach(tag => {
      if (!validTag(tag)) {
        valid = false;
        tagWarning(tag);
      }
    });
    return valid;
  };

  const tagToConnectionTag = (tag: TagModel): ConnectionTagModel => ({
    id: tag.id as number | undefined,
    name: tag.name.toLowerCase(),
    sentAs: tag.name,
  });

  const tagsToConnectionTags = (list: TagModel[]): ConnectionTagModel[] =>
    list.filter(t => validTag(t)).map(t => tagToConnectionTag(t));

  const buildOwnTag = (): TagModel => ({
    name: OWN_TAG,
    closable: false,
  });

  const tagToTagModel = (tag: ConnectionTagModel): TagModel => ({
    id: tag.id,
    name: tag.name,
    unsaved: false,
    kind: 'regular',
  });

  useEffect(() => {
    const initialTags: TagModel[] = [];

    connection.tags?.forEach(tag => {
      initialTags.push(tagToTagModel(tag));
    });

    const ownTagIdx = initialTags.findIndex(t => t.name === OWN_TAG);
    let ownTag: TagModel;

    if (ownTagIdx === -1) {
      ownTag = buildOwnTag();
    } else {
      ownTag = initialTags[ownTagIdx];
      ownTag.closable = false;
      initialTags.splice(ownTagIdx, 1);
    }
    initialTags.unshift(ownTag);

    setTags(initialTags);
  }, []);

  useEffect(() => {
    if (tagsEnabled && tags.length === 1 && tags[0].id === undefined) {
      if (cb) cb(tags); // Store initial own tag
    }
  }, [tagsEnabled]);

  const onTagChange = (tag: TagModel) => {
    if (!validTag(tag)) {
      tagWarning(tag);
      return;
    }
    const list = _.uniqBy([...tags, tag], e => e.name.toLowerCase());
    setTags(list);
    if (cb) cb(list);
  };

  const onTagRemove = (tag: TagModel) => {
    if (tag.name.toLowerCase() === OWN_TAG) return;

    const list = tags.filter(t => t.name !== tag.name.toLowerCase());

    setTags(list);
    if (cb) cb(list);
  };

  return {
    onTagChange,
    onTagRemove,
    tagsToConnectionTags,
    tags,
    availableTags,
    tagsValid,
    tagsEnabled,
  };
};
